import React, { useRef, useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { navTestId } from '@/utils/testId';
import { Tooltip } from '@/components/ui/Tooltip';
import { Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';

interface RibbonButtonProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  count?: number;
  countLabel?: string;
  onClick?: () => void;
  items?: { key: string; label: string; active?: boolean; onSelect: () => void }[];
}

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '');

const RibbonLabel: React.FC<{ label: string; singleLine?: boolean }> = ({ label, singleLine }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const cleanLabel = stripHtml(label);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(
        singleLine ? el.scrollWidth > el.clientWidth : el.scrollHeight > el.clientHeight
      );
    }
  }, [cleanLabel, singleLine]);

  const span = (
    <span
      ref={ref}
      className={cn(
        'text-center leading-tight w-full mt-1',
        singleLine ? 'truncate' : 'break-words line-clamp-2'
      )}
    >
      {cleanLabel}
    </span>
  );

  if (isTruncated) {
    return <Tooltip content={cleanLabel}>{span}</Tooltip>;
  }
  return span;
};

export const RibbonButton: React.FC<RibbonButtonProps> = ({
  icon,
  label,
  active,
  disabled,
  count,
  countLabel,
  onClick,
  items,
}) => {
  const button = (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={`ribbon-${navTestId(label)}-btn`}
      className={cn(
        'flex flex-col items-center px-2 pt-2 pb-0.5 rounded text-[10px] font-medium w-[84px] h-full transition-colors',
        items?.length && 'flex-1 min-h-0',
        active
          ? 'text-chrome-bar-fg-active'
          : 'text-chrome-bar-fg-dim hover:text-chrome-bar-fg-active hover:bg-chrome-bar-hover',
        disabled && 'opacity-40 pointer-events-none'
      )}
    >
      <span
        className={cn(
          'relative h-8 w-8 rounded flex items-center justify-center shrink-0',
          active && 'bg-chrome-bar-hover'
        )}
      >
        {icon}
        {count != null && count > 0 && (
          <span
            title={`${countLabel ?? label}: ${count}`}
            aria-label={`${countLabel ?? label}: ${count}`}
            className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-chrome-accent text-white text-[9px] font-semibold leading-4 text-center"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </span>
      <RibbonLabel label={label} singleLine={!!items?.length} />
    </button>
  );
  if (!items?.length) return button;
  return (
    <div className="flex flex-col w-[84px] h-full shrink-0">
      {button}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Rozbalit ${stripHtml(label)}`}
            className={cn(
              'h-4 w-full shrink-0 flex items-center justify-center rounded hover:bg-chrome-bar-hover focus-visible:ring-2 focus-visible:ring-primary-500',
              active ? 'text-chrome-bar-fg-active' : 'text-chrome-bar-fg-dim',
              disabled && 'opacity-40'
            )}
          >
            <ChevronDown size={12} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-48">
          {items.map((item) => (
            <DropdownMenuItem
              key={item.key}
              onSelect={item.onSelect}
              aria-current={item.active ? 'page' : undefined}
              className={cn(item.active && 'bg-primary-50 text-primary-700')}
            >
              <span className="w-4">{item.active && <Check size={14} />}</span>
              {stripHtml(item.label)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const RibbonSeparator: React.FC = () => (
  <div className="w-px self-center h-12 bg-chrome-bar-line mx-1" />
);

interface RibbonProps {
  children: React.ReactNode;
  className?: string;
}

const Ribbon: React.FC<RibbonProps> = ({ children, className }) => (
  <div
    className={cn(
      'h-[86px] bg-chrome text-chrome-bar-fg flex items-stretch gap-0.5 px-2 pb-2 relative z-50 shadow-ribbon',
      className
    )}
  >
    {children}
  </div>
);

export default Ribbon;
